import React, { useState, useEffect } from 'react';
import { styleDefinitions as styles } from '../../theme/styles';
import { X, Edit2 } from 'lucide-react';
import { WorkItem, WorkItemStatus } from '../../types';
import { db } from '../../services/database';

interface EditModalProps {
  item: WorkItem | null;
  onSave: (item: WorkItem) => void;
  onClose: () => void;
}

export const EditModal: React.FC<EditModalProps> = ({ item, onSave, onClose }) => {
  const [formData, setFormData] = useState<Partial<WorkItem>>({
    fileName: '',
    link: '',
    description: '',
    tags: [],
    status: undefined,
    level1: '',
    level2: ''
  });
  const [tagInput, setTagInput] = useState('');
  const [savedTags, setSavedTags] = useState<string[]>([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [subcategories, setSubcategories] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (item) {
      setFormData({
        ...item
      });
    }

    // Load categories
    const savedCategories = localStorage.getItem('cleanRoomCategories');
    const categoryList = savedCategories ? JSON.parse(savedCategories) : ['What I am working on', 'What I am learning', 'What I am interested'];
    setCategories(categoryList);

    // Load subcategories
    const savedSubcategories = localStorage.getItem('cleanRoomSubcategories');
    const subcategoryMap = savedSubcategories ? JSON.parse(savedSubcategories) : {};
    setSubcategories(subcategoryMap);

    // Load saved tags and collect tags from existing items
    const collectExistingTags = async () => {
      try {
        const allTags = new Set<string>();

        // Get tags from existing items in the IndexedDB database
        const items = await db.getAllWorkItems();
        console.log('[EditModal] Total items in database:', items.length);

        // Collect all unique tags from existing items
        items.forEach((item: WorkItem) => {
          if (item.tags && Array.isArray(item.tags)) {
            item.tags.forEach((tag: string) => {
              if (tag && tag.trim()) {
                console.log(`[EditModal] Found tag "${tag}" in item "${item.fileName}"`);
                allTags.add(tag.trim());
              }
            });
          }
        });

        const uniqueTags = Array.from(allTags).sort();
        console.log('[EditModal] All unique tags collected:', uniqueTags);
        setSavedTags(uniqueTags);

        // Clear old localStorage tags that might have hardcoded values
        localStorage.removeItem('focusHubSavedTags');
      } catch (e) {
        console.error('Failed to load tags:', e);
      }
    };

    collectExistingTags();
  }, [item]);

  // Handle ESC key press
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (item) {
      onSave({
        ...item,
        ...formData,
        metadata: {
          ...item.metadata,
          modified: new Date(),
          version: item.metadata.version + 1
        }
      } as WorkItem);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim()) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (index: number) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter((_, i) => i !== index) || []
    }));
  };

  if (!item) return null;

  return (
    <div style={styles.modal.overlay} onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div style={styles.modal.container} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modal.header}>
          <div style={styles.display.flexAlignCenter}>
            <div style={styles.modal.icon}>
              <Edit2 size={20} style={{ color: 'white' }} />
            </div>
            <h2 style={styles.modal.title}>Edit Item</h2>
          </div>
          <button
            onClick={onClose}
            style={styles.modal.close}
          >
            <X size={20} style={styles.typography.textSecondary} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div style={styles.modal.body}>
            <div style={styles.form.group}>
              <label style={styles.form.label}>
                Item Name
            </label>
            <input
              type="text"
              value={formData.fileName || ''}
              onChange={e => setFormData(prev => ({ ...prev, fileName: e.target.value }))}
              style={styles.form.input}
              required
            />
          </div>

          <div style={styles.form.group}>
            <label style={styles.form.label}>
              Category
            </label>
            <select
              value={formData.level1 || ''}
              onChange={e => setFormData(prev => ({ ...prev, level1: e.target.value, level2: 'Items' }))}
              style={styles.form.select}
              required
            >
              <option value="">Select a category</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div style={styles.form.group}>
            <label style={styles.form.label}>
              Subcategory
            </label>
            <select
              value={formData.level2 || ''}
              onChange={e => setFormData(prev => ({ ...prev, level2: e.target.value }))}
              style={styles.form.select}
            >
              <option value="Items">Items (Default)</option>
              {formData.level1 && subcategories[formData.level1]?.map(subcat => (
                <option key={subcat} value={subcat}>{subcat}</option>
              ))}
            </select>
          </div>

          <div style={styles.form.group}>
            <label style={styles.form.label}>
              Link
            </label>
            <input
              type="url"
              value={formData.link || ''}
              onChange={e => setFormData(prev => ({ ...prev, link: e.target.value }))}
              style={styles.form.input}
              placeholder="https://..."
            />
          </div>

          <div style={styles.form.group}>
            <label style={styles.form.label}>
              Notes
            </label>
            <textarea
              value={formData.description || ''}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              style={styles.form.textarea}
              rows={3}
            />
          </div>

          <div style={styles.form.group}>
            <label style={styles.form.label}>
              Tags
            </label>
            <div style={styles.position.relative}>
              <div style={styles.form.tagInputWrapper}>
                <input
                  type="text"
                  value={tagInput}
                  onChange={e => {
                    setTagInput(e.target.value);
                    setShowTagSuggestions(true);
                  }}
                  onFocus={() => setShowTagSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowTagSuggestions(false), 200)}
                  onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  style={styles.form.input}
                  placeholder="Add tag or select from suggestions..."
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  style={styles.form.tagAddButton}
                >
                  Add
                </button>
              </div>
              {showTagSuggestions && savedTags.length > 0 && (
                <div style={styles.form.tagSuggestionsDropdown}>
                  {savedTags
                    .filter(tag =>
                      !formData.tags?.includes(tag) &&
                      (tagInput === '' || tag.toLowerCase().includes(tagInput.toLowerCase()))
                    )
                    .slice(0, 10)
                    .map((tag, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            tags: [...(prev.tags || []), tag]
                          }));
                          setTagInput('');
                          setShowTagSuggestions(false);
                        }}
                        style={styles.form.tagSuggestionItem}
                        onMouseEnter={(e) => {
                          Object.assign(e.currentTarget.style, styles.form.tagSuggestionItemHover);
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'white';
                        }}
                      >
                        {tag}
                      </button>
                    ))}
                </div>
              )}
            </div>

            <div style={styles.form.tagContainer}>
              {formData.tags?.map((tag, index) => (
                <span
                  key={index}
                  style={styles.form.tagPill}
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(index)}
                    style={styles.form.tagRemoveButton}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div style={styles.form.group}>
            <label style={styles.form.label}>
              Status <span style={{ opacity: 0.6 }}>(Optional)</span>
            </label>
            <select
              value={formData.status || ''}
              onChange={e => setFormData(prev => ({
                ...prev,
                status: e.target.value ? e.target.value as WorkItemStatus : undefined
              }))}
              style={styles.form.select}
            >
              <option value="">No Status</option>
              <option value="Backlog">Backlog</option>
              <option value="In Progress">In Progress</option>
              <option value="Done">Done</option>
              <option value="Blocked">Blocked</option>
            </select>
            <p style={styles.form.helper}>Choose a status or leave empty to not track</p>
            </div>
          </div>

          <div style={styles.modal.footer}>
            <button
              type="button"
              onClick={onClose}
              style={styles.button.cancel}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(229, 231, 235, 0.9)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(243, 244, 246, 0.8)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={styles.button.gradient}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.35)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.25)';
              }}
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};